import { useGdprConsentCompliance } from '@/lib/gdpr-consent-flag';
import { ImportLeadsCsvDialog as MainImportLeadsCsvDialog } from './import-leads-csv-dialog.legacy';
('use client');

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useActiveOrganization } from '@/features/organization/api/get-active-organization';
import { cn } from '@/lib/utils';
import { importMarketingAuthorization } from '@borradh-workspace/labels';
import { FileSpreadsheetIcon, Loader2Icon, UploadIcon } from 'lucide-react';
import { type ReactNode, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useImportLeadsCsv } from '../api';

const MAX_FILE_BYTES = 10_485_760; // 10 MB — matches the backend cap

const XLSX_MIME =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const isXlsx = (f: File) =>
  f.name.toLowerCase().endsWith('.xlsx') || f.type === XLSX_MIME;
const isCsv = (f: File) =>
  f.name.toLowerCase().endsWith('.csv') || f.type === 'text/csv';

/** File → base64 (no data: prefix), via FileReader so big files are safe. */
const fileToBase64 = (f: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () =>
      resolve(String(reader.result).replace(/^data:[^,]*,/, ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(f);
  });

interface ImportLeadsCsvDialogProps {
  trigger?: ReactNode;
  /**
   * Optional controlled open state, so the dialog can be driven from somewhere
   * that is not its own trigger — e.g. an item inside a dropdown menu, where a
   * nested trigger does not fire reliably. Omit both to keep the previous
   * self-managed behaviour.
   */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/**
 * Upload a CSV or Excel (.xlsx) file of leads in whatever shape it comes in —
 * the backend identifies the columns (name, email, phone…) automatically, so
 * no column mapping is needed. Files are capped at 10 MB.
 */
function ConsentImportLeadsCsvDialog({
  trigger,
  open: openProp,
  onOpenChange,
}: ImportLeadsCsvDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = openProp ?? internalOpen;
  const setOpen = onOpenChange ?? setInternalOpen;
  const [file, setFile] = useState<File | null>(null);
  const [consent, setConsent] = useState(false);
  // Ticked by default: the clinic authorises marketing for the people in the
  // file. Unticked imports them for service messages only.
  const [marketing, setMarketing] = useState(true);
  const { data: organization } = useActiveOrganization();
  // Who is in the file, with NO default. This used to be an unticked
  // "these are existing clients" box, and a clinic uploading its client base
  // from the Customers page rarely saw it — every one of their clients landed
  // under Leads. The import stays disabled until the uploader answers.
  const [audience, setAudience] = useState<'clients' | 'leads' | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const { importCsv, isImporting } = useImportLeadsCsv({
    onSuccess: () => {
      setOpen(false);
      setFile(null);
      setConsent(false);
      setMarketing(true);
      setAudience(null);
    },
  });

  const acceptFile = (f: File | undefined) => {
    if (!f) return;
    if (f.name.toLowerCase().endsWith('.xls') && !isXlsx(f)) {
      toast.error('Legacy .xls isn’t supported — save it as .xlsx or .csv');
      return;
    }
    if (!isCsv(f) && !isXlsx(f)) {
      toast.error('Choose a .csv or .xlsx file');
      return;
    }
    if (f.size > MAX_FILE_BYTES) {
      toast.error('File is over 10 MB. Split it and import in parts.');
      return;
    }
    setFile(f);
  };

  const onImport = async () => {
    if (!file || !consent || !audience || isImporting) return;
    // The uploader confirms consent for the whole file, so every channel
    // defaults to consented (manual entry stays per-channel opt-in).
    const shared = {
      deduplicateBy: 'email' as const,
      onDuplicate: 'skip' as const,
      consentAcknowledgment: true,
      marketingAuthorized: marketing,
      defaultConsentEmail: true,
      defaultConsentWhatsapp: true,
      markAsClients: audience === 'clients',
    };
    if (isXlsx(file)) {
      importCsv({
        fileBase64: await fileToBase64(file),
        fileName: file.name,
        ...shared,
      });
    } else {
      importCsv({ csv: await file.text(), ...shared });
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) {
          setFile(null);
          setConsent(false);
          setMarketing(true);
          setAudience(null);
        }
      }}
    >
      {/*
        `trigger === null` means the caller drives this dialog from elsewhere
        (a dropdown item) and wants NO trigger of its own. `trigger || …`
        would treat that null as "unset" and render the default button —
        which is how two stray Import buttons ended up on the clients page.
      */}
      {trigger === null ? null : (
        <DialogTrigger asChild>
          {trigger || (
            <Button variant="outline">
              <UploadIcon className="size-4" />
              Import CSV
            </Button>
          )}
        </DialogTrigger>
      )}
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Import customers from a file</DialogTitle>
          <DialogDescription>
            CSV or Excel — columns like name, email and phone are identified
            automatically, whatever they’re called. Up to 10 MB per file.
          </DialogDescription>
        </DialogHeader>

        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            acceptFile(e.dataTransfer.files?.[0]);
          }}
          className={cn(
            'flex w-full flex-col items-center gap-2 rounded-lg border border-dashed p-8 text-center transition-colors',
            dragOver ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'
          )}
        >
          {file ? (
            <>
              <FileSpreadsheetIcon className="size-8 text-primary" />
              <span className="font-medium">{file.name}</span>
              <span className="text-xs text-muted-foreground">
                {(file.size / 1024).toFixed(0)} KB · click to choose a different
                file
              </span>
            </>
          ) : (
            <>
              <UploadIcon className="size-8 text-muted-foreground" />
              <span className="font-medium">
                Drop your CSV or Excel file here or click to browse
              </span>
              <span className="text-xs text-muted-foreground">
                .csv or .xlsx up to 10 MB
              </span>
            </>
          )}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept={`.csv,text/csv,.xlsx,${XLSX_MIME}`}
          className="hidden"
          onChange={(e) => {
            acceptFile(e.target.files?.[0]);
            e.target.value = '';
          }}
        />

        <label htmlFor="csv-consent" className="flex items-start gap-2 text-sm">
          <Checkbox
            id="csv-consent"
            checked={consent}
            onCheckedChange={(c) => setConsent(Boolean(c))}
            className="mt-0.5"
          />
          These people have agreed to be contacted.
        </label>
        <label
          htmlFor="csv-marketing"
          className="flex items-start gap-2 text-sm"
        >
          <Checkbox
            id="csv-marketing"
            checked={marketing}
            onCheckedChange={(c) => setMarketing(Boolean(c))}
            className="mt-0.5"
          />
          {importMarketingAuthorization(organization?.name)}
        </label>

        <fieldset className="flex flex-col gap-2">
          <legend className="mb-2 font-medium text-sm">
            Who is in this file?
          </legend>
          <RadioGroup
            onValueChange={(value) => setAudience(value as 'clients' | 'leads')}
            value={audience ?? ''}
          >
            <div className="flex items-start gap-2">
              <RadioGroupItem
                className="mt-0.5"
                id="csv-audience-clients"
                value="clients"
              />
              <Label
                className="flex-col items-start gap-0.5 font-normal"
                htmlFor="csv-audience-clients"
              >
                <span className="font-medium">Existing clients</span>
                <span className="text-muted-foreground text-xs">
                  People who have booked with you before. They go under Clients.
                </span>
              </Label>
            </div>
            <div className="flex items-start gap-2">
              <RadioGroupItem
                className="mt-0.5"
                id="csv-audience-leads"
                value="leads"
              />
              <Label
                className="flex-col items-start gap-0.5 font-normal"
                htmlFor="csv-audience-leads"
              >
                <span className="font-medium">New leads</span>
                <span className="text-muted-foreground text-xs">
                  People who haven’t booked yet. They go under Leads, in the
                  Imported tab.
                </span>
              </Label>
            </div>
          </RadioGroup>
        </fieldset>

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => setOpen(false)}
            disabled={isImporting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void onImport()}
            disabled={!file || !consent || !audience || isImporting}
          >
            {isImporting ? (
              <>
                <Loader2Icon className="size-4 animate-spin" />
                Importing…
              </>
            ) : (
              'Import'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ImportLeadsCsvDialog(
  props: Parameters<typeof ConsentImportLeadsCsvDialog>[0]
) {
  const enabled = useGdprConsentCompliance();
  return enabled ? (
    <ConsentImportLeadsCsvDialog {...props} />
  ) : (
    <MainImportLeadsCsvDialog {...props} />
  );
}
